import React, { useState, useRef } from "react";
import { Scanner } from "@yudiel/react-qr-scanner";
import { decodeQrData, scanFilesForQr } from "../../utils/qr-utils";
import ScannerBorderIcon from "../../resources/icons/scanner-ouline.svg";
import ScanIcon from "../../resources/icons/scan-svgrepo-com.svg";
import ScanIconHover from "../../resources/icons/scan-svgrepo-com-hover.svg";
import { verify } from "../../utils/verification-utils";
import UploadIcon from "../../resources/icons/upload-minimalistic-svgrepo-com.svg";
import UploadIconHover from "../../resources/icons/upload-minimalistic-svgrepo-com-hover.svg";
import "./Validate.css";
import toast from "react-hot-toast";
import FullScreenLoader from "../../common/FullScreenLoader/FullScreenLoader";
import useInterceptedNavigate from "../../hooks/useInterceptedNavigate";
import PaymentFailedModal from "../../common/PaymentFailedModal/PaymentFailedModal";
import ErrorIcon from "../../resources/icons/error-live-icon.svg";
import WarningIcon from "../../resources/icons/warning-live-icon.svg";
import { useLocation } from "react-router-dom";
import { POST } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import UploadConfirmationModal from "../../common/UploadConfirmationModal/UploadConfirmationModal"; // Import the new modal

const LegacyValidate = ({ setCurrentStep }) => {
  const navigate = useInterceptedNavigate();
  const location = useLocation();
  const { state } = location;
  const showPaymentFailedModal = state?.showPaymentFailedModal || false;
  const showPaymentPendingModal = state?.showPaymentPendingModal || false;
  const [activeTab, setActiveTab] = useState("upload");
  const [isScanning, setIsScanning] = useState(false);
  const [isValid, setIsValid] = useState(null);
  const [checks, setChecks] = useState([]);
  const [currentUploadIcon, setCurrentUploadIcon] = useState(UploadIcon);
  const [isValidating, setIsValidating] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const [showUploadConfirmationModal, setShowUploadConfirmationModal] =
    useState(false);
  const [uploadedImageSrc, setUploadedImageSrc] = useState("");
  const fileInputRef = useRef(null);

  const verifyCredential = (credential_id) => {
    const body = {
      document: localStorage.getItem("uploaded_image"),
      encoded_data: credential_id,
    };

    POST(EntityConstants.VERIFY_CREDENTIAL, body)
      .then((res) => {
        return res.json().then((data) => ({ res, data })); // Ensure 'data' is resolved
      })
      .then((ResData) => {
        if (ResData.res.ok) {
          setIsValidating(false);
          if (ResData.data.is_payment_required) {
            navigate(`/verifier/legacy/certificate-details`, {
              state: {
                uploadedDocument: ResData.data.uploaded_document,
              },
            });
            setCurrentStep(2);
          } else {
            navigate(`/verifier/legacy/certificate-details`, {
              state: {
                transaction_uploaded_document: ResData.data.uploaded_document,
                transaction_verification_status: ResData.data.status,
                underReviewProgrammeName: ResData.data.programme_name,
                underReviewIssuerName: ResData.data.issuer_name,
                underReviewCandidateName: ResData.data.recipient_name,
                underReviewCandidateEmail: ResData.data.recipient_email,
                certificateId: ResData.data.verification_log_id,
                document_type: ResData.data.document_type,
                verificationData: ResData.data.verification_data,
                total_verification_cost: ResData.data.total_verification_cost,
              },
            });
          }
        } else if (ResData.res.status === 400) {
          toast.error(ResData.data.message);
          setIsValidating(false);
        }
      })
      .catch((err) => {
        toast.error("Error occured while verifying credential");
        console.log("error >> ", err);
        setIsValidating(false);
      });
  };

  const decodeVcData = async (data) => {
    const decodedData = decodeQrData(data);
    const decodedDataJSON = JSON.parse(decodedData);
    const { status, checks } = await verify(decodedDataJSON);

    setChecks(checks);
    if (status === "OK") {
      setIsValid(true);
      verifyCredential(decodedDataJSON.id);
    } else if (status === "NOK") {
      setIsValid(false);
    }
  };

  const extractEncodedData = (data) => {
    const urlParamsRegex = /[?&]encoded_data=([^&]+)/;
    const urlPathRegex = /\/verify\/([a-f0-9]{64})$/;
    let match = data.match(urlPathRegex);
    if (match) {
      return match[1];
    }
    match = data.match(urlParamsRegex);
    if (match) {
      return match[1];
    }
    return null;
  };

  const validateFile = (e) => {
    setIsValidating(true);
    const input = e.target;
    const file = input.files[0];

    // Define validation criteria
    const allowedFormats = [
      "image/png",
      "image/jpeg",
      "image/jpg",
      "application/pdf",
    ];
    const minSize = 10 * 1024; // 10KB in bytes
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes

    // Check file type
    if (!allowedFormats.includes(file.type)) {
      toast.error(
        `Invalid file format. Allowed formats are: PNG, JPEG, JPG, PDF.`
      );
      console.error(
        `Invalid file format. Allowed formats are: PNG, JPEG, JPG, PDF.`
      );
      setIsValidating(false);
      return;
    }

    // Check file size
    if (file.size < minSize || file.size > maxSize) {
      setIsValidating(false);
      toast.error(`Invalid file size. File must be between 10KB and 5MB.`);
      console.error(`Invalid file size. File must be between 10KB and 5MB.`);
      return;
    }

    handleFileChange(e);
    const reader = new FileReader();
    reader.onload = () => {
      const base64Document = reader.result;
      setIsValidating(true);
      localStorage.setItem("uploaded_image", base64Document);
      setUploadedImageSrc(base64Document);
      setShowUploadConfirmationModal(true);
      setIsOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = async (e) => {
    const input = e.target;
    setIsValid(null);
    setChecks([]);
    setCurrentStep(1);

    if (input.files.length > 0) {
      const file = input.files[0];
      const scanResult = await scanFilesForQr(file);
      //if url
      if (scanResult.data) {
        setIsValidating(false);
        return toast.error(
          `Uploaded certificate is a VC.\n Please validate on "Verify" page`
        );
      }
    }
  };

  const handleUploadAgain = () => {
    setShowUploadConfirmationModal(false);
    fileInputRef.current.click();
  };

  const handleNext = () => {
    setShowUploadConfirmationModal(false);
    navigate(`/verifier/legacy/certificate-details`, {
      state: {
        uploadedDocument: localStorage.getItem("uploaded_image"),
      },
    });
    setCurrentStep(2);
  };

  const handleScan = async (data) => {
    setIsValidating(true);
    setIsValid(null);
    setChecks([]);
    setCurrentStep(2);

    if (data) {
      const decodedTempData = extractEncodedData(data[0].rawValue);
      setIsValidating(false);

      if (decodedTempData) {
        verifyCredential(decodedTempData);
      } else {
        decodeVcData(data[0].rawValue);
      }

      setIsScanning(false);

      //Updating last step
      setCurrentStep(3);
    } else {
      setIsValidating(false);
      return toast.error(
        `Invalid Certificate data.\n Please scan a different Certificate`
      );
    }
  };

  const handleScannerError = (error) => {
    const name =
      (error && typeof error === "object" && "name" in error && error.name) ||
      "";
    const message =
      (error &&
        typeof error === "object" &&
        "message" in error &&
        error.message) ||
      "";

    console.error("Scanner error:", error);
    setIsValidating(false);

    if (name === "NotFoundError" || /Requested device not found/i.test(message)) {
      toast.error("No camera found. Please use Upload instead.");
      setIsScanning(false);
      setActiveTab("upload");
      return;
    }

    if (name === "NotAllowedError" || name === "SecurityError") {
      toast.error("Camera permission denied. Please allow camera access or use Upload.");
      setIsScanning(false);
      setActiveTab("upload");
      return;
    }

    toast.error("Failed to start camera. Please use Upload instead.");
    setIsScanning(false);
    setActiveTab("upload");
  };

  const renderObject = (obj, keyPrefix = "") => {
    return Object.keys(obj).map((key, index) => {
      const value = obj[key];
      if (typeof value === "object" && value !== null) {
        // Recursively render nested objects
        return (
          <li key={keyPrefix + key + index}>
            <strong>{key}:</strong>
            <ul>{renderObject(value, keyPrefix + key)}</ul>
          </li>
        );
      } else {
        return (
          <li key={keyPrefix + key + index}>
            <strong>{key}:</strong> {String(value)}
          </li>
        );
      }
    });
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  return (
    <div className="flex flex-col items-center w-full bg-cfSecondary min-h-screen overflow-x-hidden">
      {showPaymentFailedModal && (
        <PaymentFailedModal
          modalHeading={"Payment Failed !"}
          modalMessage={`We’re sorry, your payment could not be processed. If the amount is deducted, it will be refunded to your account within 3-5 business days.`}
          isOpen={isOpen}
          closeModal={closeModal}
          modalIcon={ErrorIcon}
          paymentId={localStorage.getItem("payment_id")}
        />
      )}
      {showPaymentPendingModal && (
        <PaymentFailedModal
          modalHeading={"Payment Pending !"}
          modalMessage={`Your payment is currently being processed by our partner bank. If the payment fails and the amount is deducted, it will be refunded to your account within 3-5 business days.`}
          isOpen={isOpen}
          closeModal={closeModal}
          modalIcon={WarningIcon}
          paymentId={localStorage.getItem("payment_id")}
        />
      )}
      {showUploadConfirmationModal && (
        <UploadConfirmationModal
          imageSrc={uploadedImageSrc}
          onUploadAgain={handleUploadAgain}
          onNext={handleNext}
          isOpen={isOpen}
          closeModal={closeModal}
          isValidating={isValidating}
        />
      )}
      <div className="w-full flex flex-col">
        <div className="w-full flex flex-col items-center sm:items-start">
          <p className="sm:hidden px-4">
            Tap "Upload" to select a legacy certificate.
          </p>
        </div>
      </div>
      <div className="w-full flex flex-col xl:flex-row items-center justify-center xl:justify-between">
        <div className="w-full xl:w-full flex justify-center">
          <div className="ml-9 mr-9 mb-9 min-w-[90vw] min-h-[90vw] w-[90vw] h-[90vw] sm:min-w-[29rem] sm:min-h-[29rem] sm:w-[29rem]  sm:h-[29rem] relative">
            <img src={ScannerBorderIcon} alt="" className="w-full absolute" />
            <div className="w-full  h-full p-4 flex flex-col justify-center items-center absolute top-0 left-0">
              <div
                className={`flex flex-col justify-center items-center w-full h-full rounded-lg bg-[#3538cd18] ${
                  isScanning ? "p-0" : "p-5"
                }`}
              >
                {activeTab === "scanner" && (
                  <Scanner
                    onScan={handleScan}
                    onError={handleScannerError}
                    constraints={{ facingMode: "environment" }}
                  />
                )}
                <div className="sm:w-60 mt-[7.8rem] gap-2 h-96 flex flex-col justify-between text-bg-cfButton text-2xl font-semibold">
                  {activeTab === "upload" && (
                    <label
                      id="legacy-upload-trigger-button"
                      htmlFor="legacy-upload-input"
                      className="cursor-pointer px-14 py-[0.9rem] bg-sfSecondary border-2 border-cfPrimary hover:text-cfSecondary hover:bg-cfButton w-full self-center text-center flex items-center justify-between rounded-full m-0"
                      onMouseOver={() => setCurrentUploadIcon(UploadIconHover)}
                      onMouseOut={() => setCurrentUploadIcon(UploadIcon)}
                    >
                      <img
                        src={currentUploadIcon}
                        alt=""
                        className="w-10 h-10"
                      />
                      <p>Upload</p>
                    </label>
                  )}
                </div>
                {activeTab === "upload" && (
                  <input
                    type="file"
                    id="legacy-upload-input"
                    className="hidden"
                    onChange={validateFile}
                    ref={fileInputRef}
                  />
                )}
              </div>
            </div>
            <div className="absolute -bottom-36 sm:-bottom-24  transform -translate-x-1/2 left-1/2 w-full text-2xl font-medium text-black">
              <p className="text-center">
                Allowed file formats: PNG/JPEG/JPG/PDF
              </p>
              <p className="text-center">Min Size: 10KB | Max Size: 5MB</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegacyValidate;
